import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiLock, FiEye, FiEyeOff, FiLogOut,
  FiSave, FiRefreshCw, FiCheck, FiAlertCircle,
  FiPlus, FiEdit2, FiTrash2, FiExternalLink,
  FiImage, FiX, FiDollarSign, FiLink, FiFileText,
} from "react-icons/fi";

import FloatingAdminMenu from "./FloatMenu";

/* ─────────────────────────────────────
   API & AUTH
───────────────────────────────────── */
const API      = "https://www.medicinacondante.com/cursos.php";
const PASSWORD = "dante.admin";

const emptyForm = {
  imagen: "",
  link: "",
  nombre_curso: "",
  precio: "",
  descripcion: "",
};

/* ─────────────────────────────────────
   ESTILOS GLOBALES
───────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200;0,300;0,400;0,600;0,700;0,800;1,300&display=swap');

    :root {
      --gold:     #C9A84C;
      --gold-dim: rgba(201,168,76,0.55);
      --gold-sub: rgba(201,168,76,0.08);
      --gold-brd: rgba(201,168,76,0.20);
      --bg:       #0A0906;
      --bg-2:     #0F0E0B;
      --bg-3:     #141208;
      --line:     rgba(201,168,76,0.12);
      --ink:      #EDE0C8;
      --ink-2:    rgba(237,224,200,0.62);
      --ink-3:    rgba(237,224,200,0.32);
      --green:    #6DBF8F;
      --red:      #E07A7A;
    }

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      background: var(--bg);
      font-family: 'Plus Jakarta Sans', sans-serif;
    }

    ::placeholder {
      color: var(--ink-3);
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 300;
      font-style: italic;
    }

    textarea, input, select {
      font-family: 'Plus Jakarta Sans', sans-serif;
    }

    @keyframes adm-spin {
      to { transform: rotate(360deg); }
    }
    .adm-spin { animation: adm-spin 0.9s linear infinite; }

    @keyframes adm-pulse-dot {
      0%, 100% { opacity: 0.45; transform: scale(1); }
      50%       { opacity: 1;   transform: scale(1.4); }
    }
    .adm-pulse { animation: adm-pulse-dot 2s ease-in-out infinite; }

    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: var(--bg-2); }
    ::-webkit-scrollbar-thumb { background: var(--gold-brd); border-radius: 2px; }
  `}</style>
);

/* ─────────────────────────────────────
   MOTION VARIANTS
───────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

/* ─────────────────────────────────────
   SECTION LABEL
───────────────────────────────────── */
const SectionLabel = ({ children, className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <span style={{ display: "inline-block", width: 20, height: 1, background: "var(--gold)", opacity: 0.7 }} />
    <span style={{ fontWeight: 700, fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-3)" }}>
      {children}
    </span>
  </div>
);

/* ─────────────────────────────────────
   IMAGE PREVIEW
───────────────────────────────────── */
const ImagePreview = ({ src, onClear }) => (
  <AnimatePresence>
    {src && (
      <motion.div
        initial={{ opacity: 0, y: -8, height: 0 }}
        animate={{ opacity: 1, y: 0, height: "auto" }}
        exit={{ opacity: 0, y: -8, height: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        style={{ overflow: "hidden", marginTop: 10 }}
      >
        <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
          <img
            src={src}
            alt="preview"
            style={{
              width: "100%",
              height: 180,
              objectFit: "cover",
              display: "block",
              border: "1px solid var(--gold-brd)",
            }}
          />
          {/* Overlay gradient */}
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(to top, rgba(10,9,6,0.5) 0%, transparent 50%)",
            pointerEvents: "none",
          }} />
          {/* Cancel button */}
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            transition={{ duration: 0.18 }}
            onClick={onClear}
            style={{
              position: "absolute", top: 8, right: 8,
              width: 28, height: 28,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "rgba(10,9,6,0.85)",
              border: "1px solid rgba(224,122,122,0.35)",
              color: "var(--red)",
              cursor: "pointer",
              backdropFilter: "blur(4px)",
            }}
          >
            <FiX size={12} />
          </motion.button>
          {/* Label bottom */}
          <div style={{
            position: "absolute", bottom: 8, left: 12,
            fontSize: 9, fontWeight: 700, letterSpacing: "0.18em",
            textTransform: "uppercase", color: "rgba(237,224,200,0.55)",
          }}>
            Vista previa
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

/* ─────────────────────────────────────
   STYLED FIELD
───────────────────────────────────── */
const StyledField = ({ value, onChange, name, placeholder, type = "input", rows = 3, accept, onClearImage }) => {
  const [focused, setFocused] = useState(false);

  const base = {
    background:   "var(--bg-2)",
    color:        "var(--ink)",
    border:       `1px solid ${focused ? "rgba(201,168,76,0.6)" : "var(--line)"}`,
    outline:      "none",
    width:        "100%",
    fontFamily:   "'Plus Jakarta Sans', sans-serif",
    fontWeight:   300,
    fontSize:     14,
    lineHeight:   1.8,
    transition:   "border-color 0.25s, box-shadow 0.25s",
    boxShadow:    focused ? "0 0 0 4px rgba(201,168,76,0.05), inset 0 1px 3px rgba(0,0,0,0.3)" : "inset 0 1px 3px rgba(0,0,0,0.2)",
    borderRadius: 0,
  };

  const wrap = { position: "relative" };
  const underline = (
    <AnimatePresence>
      {focused && (
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }} animate={{ scaleX: 1, opacity: 1 }} exit={{ scaleX: 0, opacity: 0 }}
          style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg, var(--gold), transparent)", transformOrigin: "left" }}
        />
      )}
    </AnimatePresence>
  );

  if (type === "textarea") {
    return (
      <div style={wrap}>
        <textarea
          name={name} value={value || ""} onChange={onChange}
          placeholder={placeholder} rows={rows}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{ ...base, resize: "vertical", minHeight: 80, padding: "12px 16px" }}
        />
        {underline}
      </div>
    );
  }

  if (type === "file") {
    const hasImage = !!value;
    return (
      <div>
        <label
          style={{
            display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
            background: "var(--bg-2)",
            border: `1px solid ${focused ? "rgba(201,168,76,0.6)" : hasImage ? "var(--gold-brd)" : "var(--line)"}`,
            cursor: "pointer", transition: "border-color 0.25s",
          }}
          onMouseEnter={() => setFocused(true)} onMouseLeave={() => setFocused(false)}
        >
          <FiImage size={14} style={{ color: hasImage ? "var(--gold)" : "var(--gold-dim)", flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 300, color: hasImage ? "var(--ink-2)" : "var(--ink-3)", fontStyle: "italic", flex: 1 }}>
            {hasImage ? "Imagen cargada — hacé clic para cambiar" : placeholder}
          </span>
          {hasImage && (
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--green)", display: "flex", alignItems: "center", gap: 4 }}>
              <FiCheck size={9} /> Ok
            </span>
          )}
          <input type="file" accept={accept} onChange={onChange} style={{ display: "none" }} />
        </label>

        {/* Preview con botón cancelar */}
        <ImagePreview src={value} onClear={onClearImage} />
      </div>
    );
  }

  return (
    <div style={wrap}>
      <input
        name={name} value={value || ""} onChange={onChange}
        placeholder={placeholder} type="text"
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ ...base, height: 48, padding: "0 16px" }}
      />
      {underline}
    </div>
  );
};

/* ─────────────────────────────────────
   COURSE CARD
───────────────────────────────────── */
const CourseCard = ({ curso, onEdit, onDelete, index }) => {
  const [hovering, setHovering] = useState(false);

  return (
    <motion.div
      custom={index}
      variants={fadeUp}
      initial="hidden"
      animate="show"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        background:    "var(--bg-3)",
        border:        `1px solid ${hovering ? "var(--gold-brd)" : "var(--line)"}`,
        overflow:      "hidden",
        display:       "flex",
        flexDirection: "column",
        transition:    "border-color 0.3s",
        position:      "relative",
      }}
    >
      {/* Top accent */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 1,
        background: hovering
          ? "linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)"
          : "linear-gradient(90deg, transparent, rgba(201,168,76,0.15), transparent)",
        transition: "opacity 0.3s",
      }} />

      {/* Image */}
      <div style={{ position: "relative", height: 200, overflow: "hidden", background: "var(--bg-2)" }}>
        {curso.imagen ? (
          <motion.img
            src={curso.imagen} alt={curso.nombre_curso}
            animate={{ scale: hovering ? 1.04 : 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FiImage size={32} style={{ color: "var(--gold-brd)" }} />
          </div>
        )}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to top, rgba(10,9,6,0.85) 0%, transparent 55%)",
        }} />
      </div>

      {/* Content */}
      <div style={{ padding: "20px 22px 22px", display: "flex", flexDirection: "column", flex: 1, gap: 12 }}>
        <div>
          <SectionLabel style={{ marginBottom: 6 }}>Curso IDM</SectionLabel>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.01em", lineHeight: 1.25, marginTop: 8 }}>
            {curso.nombre_curso || "Sin nombre"}
          </h3>
        </div>

        {curso.precio && parseFloat(curso.precio) > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <FiDollarSign size={12} style={{ color: "var(--gold)" }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: "var(--gold)" }}>{curso.precio}</span>
          </div>
        )}

        {curso.descripcion && (
          <p style={{ fontSize: 12, fontWeight: 300, color: "var(--ink-2)", lineHeight: 1.75, flex: 1 }}>
            {curso.descripcion.length > 120 ? curso.descripcion.slice(0, 120) + "…" : curso.descripcion}
          </p>
        )}

        {curso.link && (
          <a
            href={curso.link} target="_blank" rel="noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: "var(--gold-dim)", letterSpacing: "0.1em", textTransform: "uppercase", textDecoration: "none" }}
          >
            <FiExternalLink size={11} />
            Ver curso
          </a>
        )}

        <div style={{ height: 1, background: "var(--line)", margin: "4px 0" }} />

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          <motion.button
            whileHover={{ opacity: 0.85 }} whileTap={{ scale: 0.96 }}
            onClick={() => onEdit(curso)}
            style={{
              flex: 1, height: 38, background: "var(--gold-sub)", color: "var(--gold)",
              border: "1px solid var(--gold-brd)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase",
            }}
          >
            <FiEdit2 size={11} /> Editar
          </motion.button>
          <motion.button
            whileHover={{ opacity: 0.85 }} whileTap={{ scale: 0.96 }}
            onClick={() => onDelete(curso.id)}
            style={{
              width: 38, height: 38, background: "rgba(224,122,122,0.07)",
              border: "1px solid rgba(224,122,122,0.18)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--red)",
            }}
          >
            <FiTrash2 size={12} />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────
   FORM PANEL
───────────────────────────────────── */
const FormPanel = ({ form, setForm, onSubmit, onCancel, editingId, saveState }) => {
  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setForm((prev) => ({ ...prev, imagen: reader.result }));
    reader.readAsDataURL(file);
  };

  const clearImage = () => setForm((prev) => ({ ...prev, imagen: "" }));

  const saveBtnMap = {
    idle:    { bg: "var(--gold)",            text: "#0A0906",    label: editingId ? "Actualizar curso" : "Crear curso", icon: <FiSave size={14} /> },
    saving:  { bg: "rgba(201,168,76,0.35)",  text: "var(--ink)", label: "Guardando…",      icon: <FiRefreshCw size={14} className="adm-spin" /> },
    success: { bg: "#3D6B4F",                text: "var(--ink)", label: "¡Guardado!",       icon: <FiCheck size={14} /> },
    error:   { bg: "#7B3B3B",                text: "var(--ink)", label: "Error al guardar", icon: <FiAlertCircle size={14} /> },
  };
  const btn = saveBtnMap[saveState];

  const fields = [
    { name: "nombre_curso", placeholder: "Nombre del curso",       icon: <FiFileText size={13} />,  type: "input" },
    { name: "link",         placeholder: "Link (URL de Hotmart…)", icon: <FiLink size={13} />,      type: "input" },
    { name: "precio",       placeholder: "Precio (ej: 49.99)",     icon: <FiDollarSign size={13} />, type: "input" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      style={{
        background: "var(--bg-3)", border: "1px solid var(--line)",
        padding: "clamp(24px,4vw,40px)", marginBottom: 40, position: "relative", overflow: "hidden",
      }}
    >
      {/* Top accent */}
      <div style={{ position: "absolute", top: 0, left: 32, right: 32, height: 1, background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.4), transparent)" }} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <SectionLabel>{editingId ? "Editar curso" : "Nuevo curso"}</SectionLabel>
          <h2 style={{ marginTop: 8, fontSize: "clamp(1.1rem,2.5vw,1.4rem)", fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.01em" }}>
            {editingId ? "Actualizá el curso seleccionado" : "Agregá un nuevo curso a IDM"}
          </h2>
        </div>
        {editingId && (
          <motion.button
            whileHover={{ rotate: 90 }} whileTap={{ scale: 0.88 }}
            transition={{ duration: 0.18 }}
            onClick={onCancel}
            style={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--gold-sub)", border: "1px solid var(--gold-brd)", color: "var(--gold)", cursor: "pointer" }}
          >
            <FiX size={14} />
          </motion.button>
        )}
      </div>

      <div style={{ height: 1, background: "var(--line)", marginBottom: 24 }} />

      <div style={{ display: "grid", gap: 16 }}>

        {/* Image upload con preview */}
        <div>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 8 }}>
            Imagen del curso
          </p>
          <StyledField
            type="file"
            accept="image/*"
            value={form.imagen}
            onChange={handleImage}
            onClearImage={clearImage}
            placeholder="Seleccioná una imagen…"
          />
        </div>

        {/* Text fields */}
        {fields.map(({ name, placeholder, icon, type }) => (
          <div key={name}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: "var(--gold-dim)" }}>{icon}</span>
              {placeholder.split("(")[0].trim()}
            </p>
            <StyledField
              type={type} name={name} value={form[name]}
              onChange={(e) => setForm((prev) => ({ ...prev, [name]: e.target.value }))}
              placeholder={placeholder}
            />
          </div>
        ))}

        {/* Descripcion */}
        <div>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 8 }}>
            Descripción
          </p>
          <StyledField
            type="textarea" name="descripcion" value={form.descripcion}
            onChange={(e) => setForm((prev) => ({ ...prev, descripcion: e.target.value }))}
            placeholder="Describí brevemente el curso: contenido, beneficios, a quién está dirigido…"
            rows={4}
          />
        </div>

        {/* Save button */}
        <motion.button
          whileHover={{ opacity: saveState === "idle" ? 0.87 : 1 }}
          whileTap={{ scale: 0.985 }}
          onClick={onSubmit}
          disabled={saveState === "saving"}
          style={{
            height: 52, background: btn.bg, color: btn.text, border: "none",
            cursor: "pointer", fontSize: 11, fontWeight: 700, letterSpacing: "0.14em",
            textTransform: "uppercase", display: "flex", alignItems: "center",
            justifyContent: "center", gap: 10, transition: "background 0.35s",
          }}
        >
          <AnimatePresence mode="wait">
            <motion.span key={`icon-${saveState}`}
              initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.18 }}>
              {btn.icon}
            </motion.span>
          </AnimatePresence>
          <AnimatePresence mode="wait">
            <motion.span key={`label-${saveState}`}
              initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.2 }}>
              {btn.label}
            </motion.span>
          </AnimatePresence>
        </motion.button>
      </div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════ */
const AdminCursos = () => {
  const [isAuth,        setIsAuth]        = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [showPassword,  setShowPassword]  = useState(false);
  const [errorAuth,     setErrorAuth]     = useState("");
  const [showMenu,      setShowMenu]      = useState(false);

  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [form,      setForm]      = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const [deleteId,  setDeleteId]  = useState(null);

  /* ── Auth ── */
  useEffect(() => {
    if (localStorage.getItem("admin_cursos_auth") === "true") setIsAuth(true);
  }, []);

  const handleLogin = () => {
    if (passwordInput === PASSWORD) {
      localStorage.setItem("admin_cursos_auth", "true");
      setIsAuth(true);
      setErrorAuth("");
    } else {
      setErrorAuth("Contraseña incorrecta");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_cursos_auth");
    setIsAuth(false);
  };

  /* ── Fetch ── */
  const fetchCursos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { if (isAuth) fetchCursos(); }, [isAuth]);

  /* ── Submit ── */
  const handleSubmit = async () => {
    setSaveState("saving");
    try {
      if (editingId) {
        await axios.put(`${API}?id=${editingId}`, form);
      } else {
        await axios.post(API, form);
      }
      setSaveState("success");
      setForm(emptyForm);
      setEditingId(null);
      fetchCursos();
      setTimeout(() => setSaveState("idle"), 2800);
    } catch {
      setSaveState("error");
      setTimeout(() => setSaveState("idle"), 2800);
    }
  };

  /* ── Delete ── */
  const handleDelete = (id) => setDeleteId(id);

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}?id=${deleteId}`);
      setDeleteId(null);
      fetchCursos();
    } catch (err) { console.error(err); setDeleteId(null); }
  };

  /* ── Edit ── */
  const handleEdit = (curso) => {
    setEditingId(curso.id);
    setForm({
      imagen:       curso.imagen || "",
      link:         curso.link || "",
      nombre_curso: curso.nombre_curso || "",
      precio:       curso.precio || "",
      descripcion:  curso.descripcion || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  /* ═══════════════════════════════
     RENDER
  ═══════════════════════════════ */
  return (
    <>
      <GlobalStyles />

      <div style={{ minHeight: "100vh", background: "var(--bg)", fontFamily: "'Plus Jakarta Sans', sans-serif", position: "relative" }}>

        {/* Radial glow */}
        <div style={{ position: "fixed", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse at 15% 0%, rgba(201,168,76,0.07) 0%, transparent 55%)", zIndex: 0 }} />

        {/* Top line */}
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 1, zIndex: 50, background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)" }} />

        {/* ─────── FLOAT MENU ─────── */}
        {isAuth && <FloatingAdminMenu showMenu={showMenu} setShowMenu={setShowMenu} />}

        {/* ─────── LOGIN ─────── */}
        <AnimatePresence>
          {!isAuth && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: "fixed", inset: 0, zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            >
              <div style={{ position: "absolute", inset: 0, background: "rgba(10,9,6,0.96)", backdropFilter: "blur(14px)" }} />

              <motion.div
                initial={{ scale: 0.88, y: 28, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.88, y: 28, opacity: 0 }}
                transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
                style={{ position: "relative", zIndex: 10, width: "100%", maxWidth: 360, background: "var(--bg-3)", border: "1px solid var(--line)", boxShadow: "0 32px 80px rgba(0,0,0,0.6)" }}
              >
                <div style={{ position: "absolute", top: 0, left: 32, right: 32, height: 1, background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)" }} />

                <div style={{ padding: "clamp(28px,6vw,44px)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 32 }}>
                    <div style={{ width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--line)", background: "var(--gold-sub)", color: "var(--gold)", flexShrink: 0 }}>
                      <FiLock size={16} />
                    </div>
                    <div>
                      <SectionLabel>Acceso restringido</SectionLabel>
                      <h2 style={{ marginTop: 4, fontSize: "clamp(1rem,3vw,1.15rem)", fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.01em" }}>
                        IDM Cursos Admin
                      </h2>
                    </div>
                  </div>

                  <div style={{ height: 1, background: "var(--line)", marginBottom: 24 }} />

                  <div style={{ position: "relative", marginBottom: 12 }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                      placeholder="Contraseña de acceso"
                      style={{ background: "var(--bg-2)", color: "var(--ink)", border: "1px solid var(--line)", outline: "none", width: "100%", height: 48, padding: "0 48px 0 16px", fontSize: 14, fontWeight: 300, borderRadius: 0 }}
                    />
                    <button
                      onClick={() => setShowPassword((v) => !v)}
                      style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--gold-dim)", cursor: "pointer" }}
                    >
                      {showPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                    </button>
                  </div>

                  <AnimatePresence>
                    {errorAuth && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--red)", marginBottom: 12 }}
                      >
                        <FiAlertCircle size={11} /> {errorAuth}
                      </motion.p>
                    )}
                  </AnimatePresence>

                  <motion.button
                    whileHover={{ opacity: 0.86 }} whileTap={{ scale: 0.97 }}
                    onClick={handleLogin}
                    style={{ width: "100%", padding: "14px 0", background: "var(--gold)", color: "#0A0906", fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", border: "none", cursor: "pointer" }}
                  >
                    Ingresar
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─────── DELETE CONFIRM MODAL ─────── */}
        <AnimatePresence>
          {deleteId && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            >
              <div
                onClick={() => setDeleteId(null)}
                style={{ position: "absolute", inset: 0, background: "rgba(10,9,6,0.88)", backdropFilter: "blur(10px)" }}
              />
              <motion.div
                initial={{ scale: 0.9, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 16 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                style={{ position: "relative", zIndex: 10, background: "var(--bg-3)", border: "1px solid var(--line)", padding: 32, maxWidth: 360, width: "100%" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                  <div style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(224,122,122,0.1)", border: "1px solid rgba(224,122,122,0.25)", color: "var(--red)" }}>
                    <FiTrash2 size={16} />
                  </div>
                  <div>
                    <SectionLabel>Confirmar acción</SectionLabel>
                    <h3 style={{ marginTop: 4, fontSize: 15, fontWeight: 800, color: "var(--ink)" }}>Eliminar curso</h3>
                  </div>
                </div>
                <p style={{ fontSize: 13, fontWeight: 300, color: "var(--ink-2)", lineHeight: 1.7, marginBottom: 24 }}>
                  Esta acción no se puede deshacer. ¿Querés eliminar este curso permanentemente?
                </p>
                <div style={{ display: "flex", gap: 10 }}>
                  <motion.button
                    whileHover={{ opacity: 0.8 }} whileTap={{ scale: 0.96 }}
                    onClick={() => setDeleteId(null)}
                    style={{ flex: 1, height: 42, background: "var(--gold-sub)", color: "var(--gold)", border: "1px solid var(--gold-brd)", cursor: "pointer", fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}
                  >
                    Cancelar
                  </motion.button>
                  <motion.button
                    whileHover={{ opacity: 0.8 }} whileTap={{ scale: 0.96 }}
                    onClick={confirmDelete}
                    style={{ flex: 1, height: 42, background: "rgba(224,122,122,0.15)", color: "var(--red)", border: "1px solid rgba(224,122,122,0.3)", cursor: "pointer", fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}
                  >
                    Eliminar
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─────── PANEL PRINCIPAL ─────── */}
        {isAuth && (
          <div style={{ position: "relative", zIndex: 10, maxWidth: 1200, margin: "0 auto", padding: "clamp(80px,10vw,120px) clamp(16px,5vw,48px) 96px" }}>

            {/* HEADER */}
            <motion.div variants={fadeUp} initial="hidden" animate="show" style={{ marginBottom: 48 }}>
              <SectionLabel style={{ marginBottom: 12 }}>Panel de administración</SectionLabel>
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
                <div>
                  <h1 style={{ fontSize: "clamp(1.75rem,4vw,2.8rem)", fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.02em", lineHeight: 1.04 }}>
                    Cursos <em style={{ color: "var(--gold)", fontStyle: "normal" }}>IDM</em> — Medicina
                  </h1>
                  <p style={{ marginTop: 8, fontSize: 13, fontWeight: 300, color: "var(--ink-3)", fontStyle: "italic" }}>
                    Gestioná los cursos disponibles en la plataforma
                  </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ textAlign: "right" }}>
                    <SectionLabel>Total cursos</SectionLabel>
                    <p style={{ marginTop: 4, fontSize: 20, fontWeight: 800, color: "var(--gold)" }}>{data.length}</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.93 }}
                    onClick={fetchCursos}
                    style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--line)", color: "var(--gold)", background: "transparent", cursor: "pointer" }}
                  >
                    <FiRefreshCw size={14} className={loading ? "adm-spin" : ""} />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.93 }}
                    onClick={handleLogout}
                    style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(224,122,122,0.2)", color: "var(--red)", background: "transparent", cursor: "pointer" }}
                  >
                    <FiLogOut size={14} />
                  </motion.button>
                </div>
              </div>

              <motion.div
                style={{ height: 1, marginTop: 24, background: "linear-gradient(90deg, var(--gold) 0%, transparent 100%)", opacity: 0.14, transformOrigin: "left" }}
                initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              />
            </motion.div>

            {/* FORM */}
            <FormPanel
              form={form} setForm={setForm}
              onSubmit={handleSubmit} onCancel={handleCancel}
              editingId={editingId} saveState={saveState}
            />

            {/* LOADING */}
            <AnimatePresence>
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "80px 0", gap: 20 }}
                >
                  <div className="adm-spin" style={{ width: 36, height: 36, borderRadius: "50%", border: "1px solid var(--line)", borderTopColor: "var(--gold)" }} />
                  <p style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "var(--gold-dim)" }}>
                    Cargando cursos
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* GRID */}
            {!loading && (
              <>
                {data.length === 0 ? (
                  <motion.div
                    variants={fadeUp} initial="hidden" animate="show"
                    style={{ textAlign: "center", padding: "80px 0" }}
                  >
                    <div style={{ width: 64, height: 64, margin: "0 auto 20px", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--line)", background: "var(--gold-sub)" }}>
                      <FiPlus size={24} style={{ color: "var(--gold-dim)" }} />
                    </div>
                    <p style={{ fontSize: 13, fontWeight: 300, color: "var(--ink-3)", fontStyle: "italic" }}>
                      No hay cursos cargados aún. Creá el primero desde el formulario.
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    variants={stagger} initial="hidden" animate="show"
                    style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 24 }}
                  >
                    {data.map((curso, i) => (
                      <CourseCard
                        key={curso.id}
                        curso={curso}
                        index={i}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    ))}
                  </motion.div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AdminCursos;